import CountrySelect from '@/components/Form/Shared/CountrySelect'
import DistrictSelect from '@/components/Form/Shared/DistrictSelect'
import ProvinceSelect from '@/components/Form/Shared/ProvinceSelect'
import WardSelect from '@/components/Form/Shared/WardSelect'
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { Loading, GridHookForm, Typogaphy } from 'vnetwork-ui'
import {
    getEmployeeInfo,
    updateEmployeeAddress,
} from '@/modules/Employee/services'
import { validateStep } from '../../../Profile/schema'
import { EmployeeConfimationValues } from '@/modules/Employee/types'
import { FormValues } from '../../../Confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getCurrentEmployee,
    updateCurrentEmployee,
} from '@/modules/Account/components/services'

interface AddressProps {
    employeeId?: number
    isSelf?: boolean
}

const Address: React.FC<AddressProps> = ({ employeeId, isSelf = false }) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: isSelf
            ? [QUERY_KEYS.CURRENT_USER_PROFILE]
            : [QUERY_KEYS.EMPLOYEE, id],
        queryFn: () => (isSelf ? getCurrentEmployee() : getEmployeeInfo(id)),
        enabled: isSelf || !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[200px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const { mutateAsync: updateEmployeeMutation, isPending: isUpdatePending } =
        useMutationWithHandler(
            {
                mutationFn: (isSelf
                    ? updateCurrentEmployee
                    : updateEmployeeAddress) as any,
            },
            {
                reactQueryKeys: isSelf
                    ? QUERY_KEYS.CURRENT_USER_PROFILE
                    : QUERY_KEYS.EMPLOYEES,
                successMessage: 'employee-update-success',
                errorMessage: 'error-occurred',
            }
        )

    const formControl = useForm<any>({
        defaultValues: {
            employee_country: data?.result?.employee_country || 'VN',
            employee_city: data?.result?.employee_city || '',
            employee_district: data?.result?.employee_district || '',
            employee_ward: data?.result?.employee_ward || '',
            employee_street: data?.result?.employee_street || '',
            employee_temporary_city:
                data?.result?.employee_temporary_city || '',
            employee_temporary_district:
                data?.result?.employee_temporary_district || '',
            employee_temporary_ward:
                data?.result?.employee_temporary_ward || '',
            employee_temporary_street:
                data?.result?.employee_temporary_street || '',
            temporary_same_as_permanent: false,
        },
        resolver: yupResolver(validateStep({ activeTab: 'Address' }) as any),
    })

    useEffect(() => {
        formControl.reset({
            employee_country: data?.result?.employee_country || 'VN',
            employee_city: data?.result?.employee_city || '',
            employee_district: data?.result?.employee_district || '',
            employee_ward: data?.result?.employee_ward || '',
            employee_street: data?.result?.employee_street || '',
            employee_temporary_city:
                data?.result?.employee_temporary_city || '',
            employee_temporary_district:
                data?.result?.employee_temporary_district || '',
            employee_temporary_ward:
                data?.result?.employee_temporary_ward || '',
            employee_temporary_street:
                data?.result?.employee_temporary_street || '',
            temporary_same_as_permanent: false,
        })
    }, [data])

    const onSubmit = async (values: Partial<EmployeeConfimationValues>) => {
        if (isSelf) {
            await updateEmployeeMutation(values as any)
        } else {
            await updateEmployeeMutation({
                id,
                employee: values as Partial<FormValues>,
            } as any)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-4">
            <div className="space-y-4">
                <Typogaphy
                    size="medium"
                    className="text-primary font-medium capitalize"
                >
                    {t('permanent-address')}
                </Typogaphy>
                <GridHookForm
                    formControl={formControl}
                    actions={[]}
                    subForm={true}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    data={[
                        {
                            renderEditComponent: (control) => (
                                <CountrySelect
                                    control={control}
                                    name="employee_country"
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'employee_city',
                                            ''
                                        )
                                        formControl.setValue(
                                            'employee_district',
                                            ''
                                        )
                                        formControl.setValue(
                                            'employee_ward',
                                            ''
                                        )
                                    }}
                                />
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <ProvinceSelect
                                    control={control}
                                    disabled={
                                        !formControl?.watch('employee_country')
                                    }
                                    name="employee_city"
                                    country={formControl?.watch(
                                        'employee_country'
                                    )}
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'employee_district',
                                            ''
                                        )
                                        formControl.setValue(
                                            'employee_ward',
                                            ''
                                        )
                                    }}
                                />
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <DistrictSelect
                                    control={control}
                                    name="employee_district"
                                    disabled={
                                        !formControl?.watch('employee_city')
                                    }
                                    state={formControl.watch('employee_city')}
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'employee_ward',
                                            ''
                                        )
                                    }}
                                />
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <WardSelect
                                    control={control}
                                    name="employee_ward"
                                    disabled={
                                        !formControl?.watch('employee_district')
                                    }
                                    state={formControl?.watch('employee_city')}
                                    district={formControl.watch(
                                        'employee_district'
                                    )}
                                />
                            ),
                        },
                        {
                            formItemProps: {
                                name: 'employee_street',
                                type: 'textfield',
                                label: t('street'),
                                placeholder: t('street'),
                            },
                        },
                    ]}
                />
            </div>
            <div className="space-y-4">
                <Typogaphy
                    size="medium"
                    className="text-primary font-medium capitalize"
                >
                    {t('employees.temporary_address')}
                </Typogaphy>
                <GridHookForm
                    formControl={formControl}
                    onSubmit={onSubmit}
                    actions={[
                        {
                            title: t('save-changes'),
                            type: 'submit',
                            variant: 'contained',
                            loading: isUpdatePending,
                            disabled:
                                isUpdatePending ||
                                !formControl.formState.isDirty,
                        },
                    ]}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    data={[
                        {
                            formItemProps: {
                                name: 'temporary_same_as_permanent',
                                type: 'checkbox',
                                placeholder: t(
                                    'employees.temporary_same_as_permanent'
                                ),
                                onChangeAfterHookForm: () => {
                                    if (
                                        formControl.watch(
                                            'temporary_same_as_permanent'
                                        )
                                    ) {
                                        formControl.setValue(
                                            'employee_temporary_city',
                                            formControl.watch('employee_city')
                                        )
                                        formControl.setValue(
                                            'employee_temporary_district',
                                            formControl.watch(
                                                'employee_district'
                                            )
                                        )
                                        formControl.setValue(
                                            'employee_temporary_ward',
                                            formControl.watch('employee_ward')
                                        )
                                        formControl.setValue(
                                            'employee_temporary_street',
                                            formControl.watch('employee_street')
                                        )
                                    }
                                },
                            },
                            className: 'col-span-2',
                        },
                        {
                            renderEditComponent: (control) => (
                                <CountrySelect
                                    control={control}
                                    name="employee_country"
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'employee_temporary_city',
                                            ''
                                        )
                                        formControl.setValue(
                                            'employee_temporary_district',
                                            ''
                                        )
                                        formControl.setValue(
                                            'employee_temporary_ward',
                                            ''
                                        )
                                    }}
                                />
                            ),
                            className: formControl.watch(
                                'temporary_same_as_permanent'
                            )
                                ? 'hidden'
                                : '',
                        },
                        {
                            renderEditComponent: (control) => (
                                <ProvinceSelect
                                    control={control}
                                    disabled={
                                        !formControl?.watch('employee_country')
                                    }
                                    name="employee_temporary_city"
                                    country={formControl.watch(
                                        'employee_country'
                                    )}
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'employee_temporary_district',
                                            ''
                                        )
                                        formControl.setValue(
                                            'employee_temporary_ward',
                                            ''
                                        )
                                    }}
                                />
                            ),
                            className: formControl.watch(
                                'temporary_same_as_permanent'
                            )
                                ? 'hidden'
                                : '',
                        },
                        {
                            renderEditComponent: (control) => (
                                <DistrictSelect
                                    control={control}
                                    name="employee_temporary_district"
                                    disabled={
                                        !formControl?.watch(
                                            'employee_temporary_city'
                                        )
                                    }
                                    state={formControl.watch(
                                        'employee_temporary_city'
                                    )}
                                    onChangeAfterHookForm={() => {
                                        formControl.setValue(
                                            'employee_temporary_ward',
                                            ''
                                        )
                                    }}
                                />
                            ),
                            className: formControl.watch(
                                'temporary_same_as_permanent'
                            )
                                ? 'hidden'
                                : '',
                        },
                        {
                            renderEditComponent: (control) => (
                                <WardSelect
                                    control={control}
                                    name="employee_temporary_ward"
                                    disabled={
                                        !formControl?.watch(
                                            'employee_temporary_district'
                                        )
                                    }
                                    state={formControl?.watch(
                                        'employee_temporary_city'
                                    )}
                                    district={formControl.watch(
                                        'employee_temporary_district'
                                    )}
                                />
                            ),
                            className: formControl.watch(
                                'temporary_same_as_permanent'
                            )
                                ? 'hidden'
                                : '',
                        },
                        {
                            formItemProps: {
                                name: 'employee_temporary_street',
                                type: 'textfield',
                                label: t('street'),
                                placeholder: t('street'),
                            },
                            className: formControl.watch(
                                'temporary_same_as_permanent'
                            )
                                ? 'hidden'
                                : 'col-span-2',
                        },
                    ]}
                />
            </div>
        </div>
    )
}

export default Address
